import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Cabine } from "../componentes/FerramentaUi";
import { ContagemPorMes, Ranking } from "../componentes/PeritoGraficos";
import { BOTAO_SECUNDARIO, Secao } from "../componentes/PeritoUi";
import { baixarCsv, montarCsv } from "../lib/csv";
import { hoje } from "../lib/perito/datas";
import { plural } from "../lib/perito/format";
import { folhaDoResumo, periciasPorTribunal, planilhaDasPericias, produtividadePorMes, resumoExecutivo } from "../lib/perito/relatorios";
import { usePerito } from "../lib/perito/store";

// Os Relatórios da Central do Perito (fase C), portados de `src/routes/_authenticated/relatorios.tsx`: o resumo
// executivo para imprimir ou salvar em PDF, a planilha das perícias, as perícias por tribunal e as nomeadas e
// concluídas por mês. As contas são de `lib/perito/relatorios.ts`, provadas em `tests/perito-relatorios.mjs`.
//
// O resumo sai pela impressão do navegador, numa janela própria (o padrão da casa: o currículo, o recibo do DP, a
// lista de concursos), e não pelo `@react-pdf/renderer` de lá. "Salvar como PDF" é a opção da janela de impressão,
// e a tela diz isso.

const RAIZ = "/ferramenta/central-do-perito";

export default function PeritoRelatorios() {
  const s = usePerito();
  const [recado, setRecado] = useState<string | null>(null);
  const porTribunal = useMemo(() => periciasPorTribunal(s.pericias), [s.pericias]);
  const meses = useMemo(() => produtividadePorMes(s.pericias, s.historico, 6), [s.pericias, s.historico]);
  const vazio = s.pericias.length === 0;

  const imprimirResumo = () => {
    setRecado(null);
    // `document.write` numa janela da mesma origem: por isso `folhaDoResumo` escapa todo valor. O bloqueador de
    // pop-up pode recusar, e a tela diz: botão que não responde é lido como "o sistema travou".
    const janela = window.open("", "_blank", "width=900,height=1000");
    if (!janela) {
      setRecado("O navegador bloqueou a janela de impressão. Libere as janelas deste site e tente de novo.");
      return;
    }
    janela.document.open();
    janela.document.write(folhaDoResumo(resumoExecutivo(s.pericias)));
    janela.document.close();
    janela.focus();
    janela.print();
  };
  const baixarPlanilha = () => {
    const { cabecalho, linhas } = planilhaDasPericias(s.pericias);
    baixarCsv(montarCsv(cabecalho, linhas), `pericias-${hoje()}.csv`);
  };
  const baixarTribunais = () => baixarCsv(montarCsv(["Tribunal", "Perícias"], porTribunal.map((t) => [t.nome, t.quantas])), `pericias-por-tribunal-${hoje()}.csv`);
  const baixarMeses = () => baixarCsv(montarCsv(["Mês", "Nomeadas", "Concluídas"], meses.map((m) => [m.mes, m.nomeadas, m.concluidas])), `pericias-por-mes-${hoje()}.csv`);

  return (
    <div className="flex flex-col gap-5" data-relatorios>
      <Cabine rotulo="Gestão" titulo="Relatórios" descricao="O resumo da sua atividade pericial para imprimir, e as planilhas para abrir no Excel." />

      <Secao nome="resumo-executivo" titulo="Resumo executivo"
        descricao="Os números, as perícias por etapa e por tribunal, o previsto e o recebido dos últimos 6 meses e a lista das perícias, numa folha A4.">
        {vazio ? (
          <p className="text-sm text-cinza" data-sem-pericias>
            Os relatórios ficam disponíveis depois do primeiro cadastro.{" "}
            {s.podeEscrever && <Link to={`${RAIZ}/pericias/nova`} className="font-semibold text-link hover:underline">Cadastrar uma perícia</Link>}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="pill-cta min-h-10 px-5 text-sm" onClick={imprimirResumo} data-imprimir-resumo>Imprimir o resumo</button>
              <button type="button" className={BOTAO_SECUNDARIO} onClick={baixarPlanilha} data-baixar-planilha>Baixar a planilha das perícias</button>
            </div>
            <p className="mt-3 text-xs text-cinza">
              Para ter o resumo em PDF, escolha "Salvar como PDF" na janela de impressão. A planilha tem {plural(s.pericias.length, "perícia", "perícias")}, uma por linha.
            </p>
            {recado && <p role="alert" className="mt-3 rounded-xl bg-erro-fundo p-3 text-sm text-erro-tinta" data-recado-impressao>{recado}</p>}
          </>
        )}
      </Secao>

      {!vazio && (
        // `items-start`: sem ele, o ranking curto estica até a altura do gráfico ao lado (a foto achou).
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
          <Secao nome="por-tribunal" titulo="Perícias por tribunal" descricao="Todas as perícias cadastradas, todos os tribunais">
            <Ranking itens={porTribunal.map((t) => ({ nome: t.nome, valor: t.quantas }))} />
            <div className="mt-4 border-t border-borda pt-4">
              <button type="button" className={`${BOTAO_SECUNDARIO} min-h-9 text-xs`} onClick={baixarTribunais} data-baixar-tribunais>Baixar a planilha</button>
            </div>
          </Secao>
          <Secao nome="por-mes" titulo="Nomeadas e concluídas por mês"
            descricao="Os últimos 6 meses. Concluída é a perícia que foi para a etapa 10 naquele mês.">
            <ContagemPorMes meses={meses} />
            <div className="mt-4 border-t border-borda pt-4">
              <button type="button" className={`${BOTAO_SECUNDARIO} min-h-9 text-xs`} onClick={baixarMeses} data-baixar-meses>Baixar a planilha</button>
            </div>
          </Secao>
        </div>
      )}
    </div>
  );
}
